import { SignInPrompt } from '@/features/account/SignInPrompt';
import { Icon } from '@/components/ui/icon';
import { AppScreen, Card, IconAction } from '@/components/ui/scene';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/lib/auth/auth';
import { APP_CONFIG } from '@/lib/app/config';
import { APIError } from '@/lib/api/APIError';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import {
  ProfileVisibilityFriends,
  ProfileVisibilityPublic,
  type ProfileVisibility,
} from '@/lib/api/apiTypes';
import { useDataExport } from '@/features/account/useDataExport';
import { showToast } from '@/lib/useToastStore';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { canSyncPreciseLocation } from '@/lib/location/locationPrivacy';
import { cn } from '@/lib/utils';
import { normalizeExternalHTTPURL } from '@/features/events/eventMobileHelpers';
import { router } from 'expo-router';
import {
  ArrowLeftIcon,
  DownloadIcon,
  Globe2Icon,
  type LucideIcon,
  MailIcon,
  MapPinIcon,
  ShieldIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Linking, Pressable, ScrollView, View } from 'react-native';
import { DESIGN_TOKENS } from '@/lib/design/theme';

export default function SettingsPrivacyScreen() {
  const session = useAuth((state) => state.session);
  const setProfileVisibility = useAuth((state) => state.setProfileVisibility);
  const updateLocationTrackingEnabled = useAuth((state) => state.updateLocationTrackingEnabled);
  const isDesktop = useIsDesktop();
  const dataExportMutation = useDataExport();
  const locationTrackingEnabled = canSyncPreciseLocation(session?.me.profile ?? {});
  const profileVisibility =
    session?.me.profile.visibility ??
    (session?.me.profile.is_public ? ProfileVisibilityPublic : ProfileVisibilityFriends);
  const [exportError, setExportError] = React.useState<string | null>(null);
  const [pendingProfileVisibility, setPendingProfileVisibility] =
    React.useState<ProfileVisibility | null>(null);

  async function handleProfileVisibility(nextVisibility: ProfileVisibility) {
    if (nextVisibility === profileVisibility || pendingProfileVisibility) {
      return;
    }

    setPendingProfileVisibility(nextVisibility);
    try {
      await setProfileVisibility(nextVisibility);
      showToast({
        message:
          nextVisibility === ProfileVisibilityPublic
            ? 'Your profile is public now.'
            : 'Only mutual follows can see your plans now.',
      });
    } catch (error) {
      showToast({
        message: getAPIErrorRecoveryCopy(
          error,
          'profile',
          'Unable to update profile visibility right now.'
        ),
      });
    } finally {
      setPendingProfileVisibility(null);
    }
  }

  async function handleToggleLocationTracking(nextValue: boolean) {
    try {
      await updateLocationTrackingEnabled(nextValue);
      showToast({
        message: nextValue
          ? 'Nearby browsing will use your live location again.'
          : 'Radious will stop sending new location updates to the server.',
      });
    } catch (error) {
      showToast({
        message: getAPIErrorRecoveryCopy(
          error,
          'profile',
          'Unable to update privacy settings right now.'
        ),
      });
    }
  }

  async function handleDownloadData() {
    setExportError(null);
    try {
      const result = await dataExportMutation.mutateAsync();
      const downloadURL = normalizeExternalHTTPURL(result.download_url);
      if (!downloadURL) {
        setExportError('Your export link was not ready. Try again in a moment.');
        return;
      }
      await Linking.openURL(downloadURL);
      showToast({ message: 'Your data export is ready for download.' });
    } catch (error) {
      if (error instanceof APIError && error.status === 429) {
        // Strip the "rate limited: " sentinel prefix — server includes the human-readable part after it
        const msg = error.message.replace(/^rate limited:\s*/i, '');
        setExportError(msg.charAt(0).toUpperCase() + msg.slice(1) + '.');
      } else {
        setExportError(
          getAPIErrorRecoveryCopy(error, undefined, 'Unable to prepare your data export right now.')
        );
      }
    }
  }

  return (
    <AppScreen>
      <ScrollView
        contentContainerStyle={{ paddingBottom: isDesktop ? 48 : 120 }}
        showsVerticalScrollIndicator={false}>
        <View
          style={
            isDesktop
              ? { maxWidth: 760, width: '100%', alignSelf: 'center', paddingHorizontal: 32 }
              : undefined
          }>
          <View className={isDesktop ? 'pt-4' : 'px-4 pt-4'}>
            <View className="flex-row items-center justify-between">
              <IconAction icon={ArrowLeftIcon} onPress={() => router.back()} />
              <Text className="text-3xl font-extrabold tracking-tight">Privacy</Text>
              <View className="size-11" />
            </View>
          </View>

          {!session ? (
            <View className={isDesktop ? 'pt-8' : 'px-4 pt-8'}>
              <SignInPrompt
                title="Manage your privacy."
                body="Sign in to update privacy settings."
              />
            </View>
          ) : (
            <View className={isDesktop ? 'pt-8' : 'px-4 pt-6'}>
              <PrivacySection title="Profile">
                <ProfileVisibilityOption
                  active={profileVisibility === ProfileVisibilityPublic}
                  disabled={Boolean(pendingProfileVisibility)}
                  icon={Globe2Icon}
                  label="Public"
                  description="Anyone can see your profile and plans."
                  onPress={() =>
                    handleProfileVisibility(ProfileVisibilityPublic).catch(() => undefined)
                  }
                />
                <ProfileVisibilityOption
                  active={profileVisibility === ProfileVisibilityFriends}
                  disabled={Boolean(pendingProfileVisibility)}
                  icon={ShieldIcon}
                  label="Friends only"
                  description="Only mutual follows see your plans."
                  onPress={() =>
                    handleProfileVisibility(ProfileVisibilityFriends).catch(() => undefined)
                  }
                />
              </PrivacySection>

              <PrivacySection title="Location" className="mt-6">
                <View className="flex-row items-center gap-3 px-5 py-4">
                  <Icon as={MapPinIcon} size={17} className="text-foreground" />
                  <View className="flex-1 gap-0.5">
                    <Text className="font-medium text-foreground">Use my location</Text>
                    <Text variant="small" className="text-muted-foreground">
                      When off, Radious uses your saved market and stops receiving location updates.
                    </Text>
                  </View>
                  <Switch
                    checked={locationTrackingEnabled}
                    onCheckedChange={(value) =>
                      handleToggleLocationTracking(value).catch(() => undefined)
                    }
                  />
                </View>
              </PrivacySection>

              <PrivacySection title="Your Data" className="mt-6">
                <Pressable
                  className="flex-row items-center gap-3 border-b border-border px-5 py-4"
                  disabled={dataExportMutation.isPending}
                  onPress={() => handleDownloadData().catch(() => undefined)}>
                  <Icon as={DownloadIcon} size={17} className="text-foreground" />
                  <View className="flex-1 gap-0.5">
                    <Text className="font-medium text-foreground">
                      {dataExportMutation.isPending ? 'Preparing export…' : 'Download my data'}
                    </Text>
                    <Text variant="small" className="text-muted-foreground">
                      JSON export of your profile, RSVPs, saves, follows, and comments.
                    </Text>
                  </View>
                  {dataExportMutation.isPending ? (
                    <ActivityIndicator size="small" color={DESIGN_TOKENS.colors.textMuted} />
                  ) : null}
                </Pressable>
                <View className="px-5 py-3">
                  {exportError ? (
                    <Text variant="small" className="text-primary">
                      {exportError}
                    </Text>
                  ) : (
                    <Text variant="small" className="text-muted-foreground">
                      Links stay active 24 hours · available once every 7 days
                    </Text>
                  )}
                </View>
              </PrivacySection>

              <PrivacySection title="Policy" className="mt-6">
                <Pressable
                  className="flex-row items-center gap-3 border-b border-border px-5 py-4"
                  onPress={() => router.push('/privacy-policy')}>
                  <Icon as={ShieldIcon} size={17} className="text-foreground" />
                  <Text className="flex-1 font-medium text-foreground">Privacy Policy</Text>
                </Pressable>
                <Pressable
                  className="flex-row items-center gap-3 px-5 py-4"
                  onPress={() =>
                    Linking.openURL(`mailto:${APP_CONFIG.supportEmail}`).catch(() => undefined)
                  }>
                  <Icon as={MailIcon} size={17} className="text-foreground" />
                  <View className="flex-1">
                    <Text className="font-medium text-foreground">Contact privacy support</Text>
                    <Text variant="small" className="text-muted-foreground">
                      {APP_CONFIG.supportEmail}
                    </Text>
                  </View>
                </Pressable>
              </PrivacySection>
            </View>
          )}
        </View>
      </ScrollView>
    </AppScreen>
  );
}

function PrivacySection({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <View className={className}>
      <Text variant="small" className="mb-3 px-1 uppercase tracking-[1.6px] text-muted-foreground">
        {title}
      </Text>
      <Card tone="default" className="overflow-hidden p-0">
        {children}
      </Card>
    </View>
  );
}

function ProfileVisibilityOption({
  active,
  disabled,
  icon,
  label,
  description,
  onPress,
}: {
  active: boolean;
  disabled: boolean;
  icon: LucideIcon;
  label: string;
  description: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      className={cn(
        'flex-row items-center gap-3 border-b border-border px-5 py-4 active:opacity-80',
        active && 'bg-primary/10',
        disabled && 'opacity-60'
      )}
      disabled={disabled}
      onPress={onPress}>
      <Icon as={icon} size={17} className={active ? 'text-primary' : 'text-foreground'} />
      <View className="flex-1 gap-0.5">
        <Text className={cn('font-medium', active ? 'text-primary' : 'text-foreground')}>
          {label}
        </Text>
        <Text variant="small" className="text-muted-foreground">
          {description}
        </Text>
      </View>
      {active ? (
        <Text variant="small" className="font-semibold text-primary">
          Active
        </Text>
      ) : null}
    </Pressable>
  );
}
